import { useAdminAccess } from "src/hooks/useAdminAccess";
import { DndContext, MouseSensor, pointerWithin, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { compact, groupBy, orderBy } from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { AppEventPublisher } from "../../AppEventPublisher.js";
import Auth from "../../Auth.js";
import Project, { projectUrl } from "../../models/Project.js";
import { useSeriesStore } from "../../stores/seriesStore.js";
import { useStickyTourStore } from "../../stores/stickyTourStore.js";
import { useSudowriteStore } from "../../stores/sudowriteStore.js";
import ActionMenu from "../ActionMenu";
import { Button } from "../Button/index.js";
import DotsActionMenu from "../DotsActionMenu/index.js";
import FeaturebaseChangelog from "../FeaturebaseChangelog/index.js";
import IconButton from "../IconButton/index.jsx";
import ImportNovel from "../Modals/ImportNovel/index.js";
import ExitTourButton from "../StickyTour/ExitTourButton.js";
import StickyNoteAnchor from "../StickyTour/StickyNoteAnchor.js";
import { AnchorIds, TourIds, WriteRewriteTourVariations } from "../StickyTour/TourTypes.ts";
import { SERIES_FOLDER_INTRO_TOUR } from "../StickyTour/Tours/SeriesFolderIntroTour.tsx";
import SudowriteLogo from "../SudowriteLogo";
import { SudoTooltip } from "../../sudokit/components/SudoTooltip";
import { useToast } from "../ToastWrapper/index.js";
import useImportSpeedPerceptionExperiment, { isImportSpeedPerceptionActive } from "../../hooks/useImportSpeedPerceptionExperiment";
import { isHomePath } from "../../utils/isHomePath";
import { wouldCreateCycle } from "~shared/utils/wouldCreateCycle";
import CardStack from "./CardStack/index.js";
import CleanCopyCard from "./CleanCopyCard/index.js";
import ContinuityCard from "./ContinuityCard/index.js";
import FolderCard from "./FolderCard/index.js";
import { hasReachedTrialImportLimit, TRIAL_IMPORT_LIMIT_TOOLTIP } from "./importLimit.js";
import ImportReportCard from "./ImportReportCard/index.tsx";
import NewProjectCard from "./NewProjectCard";
import OpeningFolderToast from "./OpeningFolderToast";
import POVTenseCard from "./POVTenseCard/index.tsx";
import ProjectCard from "./ProjectCard/index.js";
import ProsePreferencesCard from "./ProsePreferencesCard/index.js";
import ResourcesCard from "./ResourcesCard/index.js";
import SeriesContinuity from "./SeriesContinuity/index.js";
import { createGeneratedProject } from "./createGeneratedProject.js";
import { arrangeNewProjectFlowEntries, hasExistingSeries, shouldStartSeriesFolderIntro } from "./newProjectFlow.js";
import { useAllFoldersStore } from "./store.js";
import { Container, FoldersContainer, SeriesFolderProjectsAnchor } from "./styles.js";
import { getActionsMoveToFolder, getFolder, getParents } from "./utils.js";

function BreadcrumbItem({ folder, onClick }) {
  const { setNodeRef, active, isOver } = useDroppable({ id: folder.id });

  return (
    <p
      ref={setNodeRef}
      onClick={onClick}
      title={folder.title}
      className={classNames({ active, isOver })}
    >
      {folder.title}
    </p>
  );
}

function BreadcrumbItemTrash({ onClick }) {
  return (
    <p
      onClick={onClick}
      title="Deleted Projects"
    >
      Deleted Projects
    </p>
  );
}

function toEntries(folders, projects) {
  const entries = [
    ...folders.filter((folder) => !folder.archived).map((folder) => ({ type: "folder", folder })),
    ...projects.filter((project) => !project.archived).map((project) => ({ type: "project", project })),
  ];

  return orderBy(
    entries,
    (entry) => {
      if (entry.type === "project") {
        return entry.project.last_active;
      } else if (entry.type === "folder") {
        return entry.folder.last_active;
      }
    },
    "desc",
  );
}

function searchEntries(foldersById, projectsById, searchQuery, deleted) {
  const folders = Object.values(foldersById).filter(
    (folder) => folder.parent_folder_id !== null && folder.title.toLowerCase().includes(searchQuery.toLowerCase()) && folder.deleted === deleted && !folder.archived,
  );
  const projects = Object.values(projectsById).filter((project) => project.title.toLowerCase().includes(searchQuery.toLowerCase()) && project.deleted === deleted && !project.archived);
  return toEntries(folders, projects);
}

function getEntries(foldersById, projectsById, foldersByParentId, projectsByFolderId, folder, deleted) {
  if (!folder) {
    return [];
  }

  let folders = [];
  let projects = [];

  if (deleted && !folder.parent_folder_id) {
    // Root-level trash view: show only top-level deleted items
    // (items whose parent folder is NOT deleted, so they appear at the top level)
    folders = Object.values(foldersById).filter((f) => {
      const parent = f.parent_folder_id ? foldersById[f.parent_folder_id] : null;
      return !parent || !parent.deleted;
    });
    projects = Object.values(projectsById).filter((p) => {
      const parentFolder = p.folder_id ? foldersById[p.folder_id] : null;
      return !parentFolder || !parentFolder.deleted;
    });
  } else if (deleted || folder.deleted) {
    // Inside a deleted folder: show its direct children (which are also deleted via cascade)
    folders = foldersByParentId[folder.id] ?? [];
    projects = projectsByFolderId[folder.id] ?? [];
  } else {
    folders = foldersByParentId[folder.id] ?? [];
    projects = projectsByFolderId[folder.id] ?? [];
  }

  const targetDeleted = deleted || folder.deleted;

  return toEntries(
    folders.filter((folder) => folder.deleted === targetDeleted && !folder.archived),
    projects.filter((project) => project.deleted === targetDeleted && !project.archived),
  );
}

function getNestedProjects(foldersByParentId, projectsByFolderId, folder, deleted = false) {
  let projects = [];

  function explore(folder) {
    const projectsInFolder = projectsByFolderId[folder.id] ?? [];
    projects.push(...projectsInFolder.filter((project) => !project.archived && project.deleted === deleted));

    const folders = foldersByParentId[folder.id] ?? [];
    for (const folder of folders) {
      explore(folder);
    }
  }

  explore(folder);

  return projects;
}

function formatCount(count, type) {
  return count > 0 ? `${count} ${type}${count <= 1 ? "" : "s"}` : null;
}

const BASE_PROMO_CARDS = ["resources-card"];

function getEnabledPromoCards(userData, subscription) {
  const disabledPromoCards = userData?.data?.disabledPromoCards ?? [];
  const cards = [...BASE_PROMO_CARDS];

  if (subscription.hasAccessToPOVAndTenseBeta) {
    cards.push("pov-tense-card");
  }

  if (subscription.hasAccessToChapterGlueBeta) {
    cards.push("continuity-card");
  }

  if (subscription.hasAccessToCleanCopy) {
    cards.push("clean-copy-card");
  }

  cards.push("import-report-card");

  return cards.filter((card) => !disabledPromoCards.includes(card));
}

function PromoCard({ promoCard, onClose, isAnyImporting }) {
  switch (promoCard) {
    case "prose-preferences-card":
      return <ProsePreferencesCard onClose={onClose} />;
    case "resources-card":
      return <ResourcesCard onClose={onClose} />;
    case "clean-copy-card":
      return <CleanCopyCard onClose={onClose} />;
    case "continuity-card":
      return <ContinuityCard onClose={onClose} />;
    case "pov-tense-card":
      return <POVTenseCard onClose={onClose} />;
    case "import-report-card":
      return <ImportReportCard onClose={onClose} />;
    default:
      return null;
  }
}

export default function AppHome({ deleted, helpMenu, settingsPanel, user, userData, userMetadata }) {
  const { can } = useAdminAccess();
  const [importNovelOpen, setImportNovelOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [hideSearch, setHideSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [folderTitle, setFolderTitle] = useState("");
  const [emptySignupProjectId, setEmptySignupProjectId] = useState(null);
  const [isPromoStackRestored, setIsPromoStackRestored] = useState(false);
  const history = useHistory();
  const isImportDisabled = useSudowriteStore((state) => state.isImportDisabled);
  const contents = useSeriesStore((state) => state.contents);
  const setContents = useSeriesStore((state) => state.setContents);
  const resetSeries = useSeriesStore((state) => state.resetSeries);
  const subscription = useSudowriteStore((state) => state.subscription);
  const newProjectFlow = subscription?.hasAccessToNewProjectFlow;
  const importSpeedPerceptionExperiment = useImportSpeedPerceptionExperiment();
  const isTrialImportLimitReached = hasReachedTrialImportLimit(subscription, userMetadata, isImportDisabled);
  const showImportLimitTooltip = !deleted && isTrialImportLimitReached;
  const importButtonDisabled = deleted || isTrialImportLimitReached;
  const isTourActive = useStickyTourStore((state) => state.isTourActive);
  const activeTourId = useStickyTourStore((state) => state.activeTourId);
  const activeTourVariation = useStickyTourStore((state) => state.activeTourVariation);
  const currentStepIndex = useStickyTourStore((state) => state.currentStepIndex);
  const tourPhase = useStickyTourStore((state) => state.phase);
  const startTour = useStickyTourStore((state) => state.startTour);
  const endTour = useStickyTourStore((state) => state.endTour);

  const { setToast, dismissToast } = useToast();

  const { folderId } = useParams();
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("openImport") !== "1") return;

    if (!isTrialImportLimitReached) {
      setImportNovelOpen(true);
    }

    searchParams.delete("openImport");
    history.replace({
      pathname: location.pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : "",
    });
  }, [history, isTrialImportLimitReached, location.pathname, location.search]);

  const setFolderId = (folderId, state) => {
    if (deleted) {
      if (folderId) {
        history.push(`/app/projects/deleted/${folderId}`, state);
      } else {
        history.push("/app/projects/deleted", state);
      }
    } else {
      if (folderId) {
        history.push(`/app/folder/${folderId}`, state);
      } else {
        history.push("/app", state);
      }
    }
  };

  const openCreatedFolder = (createdFolder, isSeries, { showSeriesFolderIntro = false } = {}) => {
    const openFolder = ({ auto = false } = {}) => {
      // Auto-open only while the user remains on projects home (matches Import Novel).
      // Manual Go/Open always navigates. Return false so the toast can retry.
      if (auto && !isHomePath(history.location.pathname)) {
        return false;
      }
      dismissToast();
      setFolderId(
        createdFolder.id,
        showSeriesFolderIntro ? { showSeriesFolderIntro: true } : undefined,
      );
      return true;
    };

    setToast(
      <OpeningFolderToast
        isSeries={isSeries}
        onOpen={openFolder}
      />,
      {
        dismissible: true,
        dismissButtonVariant: "inverted",
        durationMs: Infinity,
      }
    );
  };

  const enabledPromoCards = getEnabledPromoCards(userData, subscription);

  // The fly-in only plays for the remount that follows undoing the last card.
  useEffect(() => {
    if (enabledPromoCards.length > 0) {
      setIsPromoStackRestored(false);
    }
  }, [enabledPromoCards.length]);

  // Cards hidden during this visit, most recent last, so Undo can step back through them.
  const hiddenPromoCardsRef = useRef([]);

  const hidePromoCard = (promoCard) => {
    hiddenPromoCardsRef.current.push(promoCard);
    userData.update({ disabledPromoCards: [...userData.data.disabledPromoCards, promoCard] });
    showHiddenPromoCardToast();
  };

  const undoHidePromoCard = () => {
    const promoCard = hiddenPromoCardsRef.current.pop();

    setIsPromoStackRestored(getEnabledPromoCards(userData, subscription).length === 0);
    userData.update({ disabledPromoCards: userData.data.disabledPromoCards.filter((card) => card !== promoCard) });

    if (hiddenPromoCardsRef.current.length > 0) {
      showHiddenPromoCardToast();
    } else {
      dismissToast();
    }
  };

  const showHiddenPromoCardToast = () => {
    setToast(
      <>
        Card hidden. <a onClick={undoHidePromoCard}>Undo</a>
      </>,
    );
  };

  const disableWelcomeCard = () => {
    if (enabledPromoCards.includes("onboarding-card")) {
      userData.update({ disabledPromoCards: [...userData.data.disabledPromoCards, "onboarding-card"] });
    }
  };

  const {
    data,
    update,
    createFolder,
    deleteFolder,
    restoreFolder,
    moveFolder,
    moveProject,
    deleteProject,
    restoreProject,
    renameFolder,
    archiveDeleteProject,
    archiveDeleteFolder,
    duplicateProject,
    cancelImport,
    setProjectCover,
    removeProjectCover,
    renameProject,
  } = useAllFoldersStore();

  const { foldersById, projectsById } = data(user.uid);

  const isAnyImporting = Object.values(projectsById).some((project) => project.is_importing);

  useEffect(() => {
    if (hideSearch) {
      const timeout = setTimeout(() => {
        setShowSearch(false);
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [hideSearch]);

  useEffect(() => {
    update();
  }, [update]);

  useEffect(() => {
    Auth.fetchWithIdToken("/api/projects/list", { source: "empty-signup" }).then(({ projects }) => {
      if (Array.isArray(projects) && projects.length > 0) {
        setEmptySignupProjectId(projects[0].id);
      }
    });
  }, []);

  const mutate = useCallback(() => {
    update();
  }, [update]);

  const mouseSensor = useSensor(MouseSensor, {
    // Require the mouse to move by 10 pixels before activating
    activationConstraint: {
      distance: 10,
    },
  });

  const sensors = useSensors(mouseSensor);

  const foldersByParentId = groupBy(Object.values(foldersById), "parent_folder_id");
  const projectsByFolderId = groupBy(Object.values(projectsById), "folder_id");

  const folder = getFolder(foldersById, folderId);
  const parents = getParents(foldersById, folder?.id);

  useEffect(() => {
    if (
      !shouldStartSeriesFolderIntro({
        newProjectFlow,
        locationState: location.state,
        folder,
        phase: tourPhase,
      })
    ) {
      return;
    }

    const nextLocationState = { ...location.state };
    delete nextLocationState.showSeriesFolderIntro;
    history.replace({ ...location, state: nextLocationState });
    startTour(SERIES_FOLDER_INTRO_TOUR);
  }, [folder, history, location, newProjectFlow, startTour, tourPhase]);

  useEffect(() => {
    if (
      activeTourId !== TourIds.SERIES_FOLDER_INTRO ||
      Object.keys(foldersById).length === 0 ||
      folder?.series_id
    ) {
      return;
    }

    endTour();
  }, [activeTourId, endTour, folder, foldersById]);

  const entries = searchQuery ? searchEntries(foldersById, projectsById, searchQuery, deleted) : getEntries(foldersById, projectsById, foldersByParentId, projectsByFolderId, folder, deleted);
  const projectCount = entries.filter((entry) => entry.type === "project").length;
  const folderCount = entries.filter((entry) => entry.type === "folder").length;
  const isHomePage = folder?.parent_folder_id === null;
  const folderProjects = entries.reduce((acc, entry) => (entry.type === "project" ? [...acc, entry.project] : acc), []);

  useEffect(() => {
    resetSeries(); // when the folder changes, clear out current series data
  }, [folder, resetSeries]);

  useEffect(() => {
    if (editingFolderId) {
      renameFolder(editingFolderId, folderTitle);
      setEditingFolderId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only commit on navigation, not on every keystroke
  }, [folderId]);

  // Redirect to /app if the folder doesn't exist (user doesn't have access)
  useEffect(() => {
    const hasFoldersLoaded = Object.keys(foldersById).length > 0;
    if (folderId && hasFoldersLoaded && !folder) {
      history.replace("/app");
    }
  }, [folderId, foldersById, folder, history]);

  useEffect(() => {
    AppEventPublisher.projects.viewProjectHome();
  }, []);

  const getExtendedEntries = () => {
    const currentEntries = [...entries];

    if (isTourActive && emptySignupProjectId && activeTourVariation === WriteRewriteTourVariations.ONBOARDING_IMPORTED_PROJECT) {
      const projectIndex = currentEntries.findIndex((entry) => entry.type === "project" && entry.project.firebase_id === emptySignupProjectId);

      if (projectIndex > -1) {
        const [projectEntry] = currentEntries.splice(projectIndex, 1);
        currentEntries.splice(1, 0, projectEntry);
      }
    }

    const includePromoCard = enabledPromoCards.length > 0 && isHomePage && !searchQuery && !deleted;

    if (newProjectFlow) {
      return arrangeNewProjectFlowEntries(currentEntries, {
        includeNewCard: Boolean(folder) && !searchQuery && !deleted,
        includePromoCard,
      });
    }

    if (includePromoCard) {
      const firstTwoEntries = currentEntries.slice(0, 2);
      const remainingEntries = currentEntries.slice(2);
      return [...firstTwoEntries, { type: "stack" }, ...remainingEntries];
    }
    return currentEntries;
  };

  const create = async ({ openBrainDump = false } = {}) => {
    if (!folder) {
      return;
    }

    const title = projectCount > 0 ? `Project ${projectCount}` : "Untitled Project";

    const project = await Project.create({ title, folderId: folder.id });

    // TODO: Optimistically update the folder
    mutate();

    window.edgetag("tag", "NewProject");
    history.push(
      `${projectUrl(project)}${openBrainDump ? "?sb=1" : ""}`,
      openBrainDump ? { showGeneratedProjectStoryBibleIntro: true } : undefined,
    );
  };

  const createBlankProject = async () => {
    if (!folder) {
      return;
    }

    setToast(<span role="status" aria-label="Creating project...">Creating project...</span>, { durationMs: Infinity });

    try {
      await create();
      dismissToast();
    } catch (error) {
      console.error("Failed to create blank project", error);
      setToast("Failed to create project. Please try again.");
    }
  };

  const actionsNew = {
    newProject: {
      label: "Project",
      icon: (
        <SVG
          width={24}
          height={24}
          src="/assets/project.svg"
          className="project"
          aria-hidden="true"
        />
      ),
      onClick: () => {
        AppEventPublisher.projects.createProject();
        createBlankProject();
      },
    },
    newFolder: {
      label: "Folder",
      icon: (
        <SVG
          width={24}
          height={24}
          src="/assets/folder.svg"
          className="folder"
          aria-hidden="true"
        />
      ),
      onClick: () => {
        AppEventPublisher.projects.createFolder();
        createFolder(folder?.id);
      },
    },
    newSeries: {
      label: "Series",
      icon: (
        <SVG
          width={24}
          height={24}
          src="/assets/series.svg"
          className="series"
          aria-hidden="true"
        />
      ),
      onClick: () => {
        AppEventPublisher.projects.createSeries();
        createFolder(folder?.id, true); // flag indicating a series folder
      },
    },
  };

  const actionsHeader = {
    moveToFolder: {
      label: "Move to...",
      icon: (
        <SVG
          width={24}
          height={24}
          src="/assets/move-to.svg"
          className="folder"
          aria-hidden="true"
        />
      ),
      onClick: () => {
        AppEventPublisher.projects.moveFolder();
        return getActionsMoveToFolder(
          foldersById,
          foldersByParentId,
          (folderId) => {
            moveFolder(folder?.id, folderId);
          },
          folder?.id,
          false,
        );
      },
    },
    deleteFolder: {
      label: "Delete",
      danger: true,
      icon: (
        <SVG
          width={24}
          height={24}
          src="/assets/delete.svg"
          className="trash"
          aria-hidden="true"
        />
      ),
      onClick: () => {
        AppEventPublisher.projects.deleteFolder();
        deleteFolder(folder?.id);
        setFolderId(folder?.parent_folder_id);
      },
    },
  };

  const handleDragEnd = async (event) => {
    if (event.over && event.active && event.active.id !== event.over.id) {
      if (event.active.data.current.type === "project") {
        moveProject(event.active.id, event.over.id);
      } else if (event.active.data.current.type === "folder") {
        if (wouldCreateCycle((id) => foldersById[id]?.parent_folder_id, event.active.id, event.over.id)) {
          return;
        }
        moveFolder(event.active.id, event.over.id);
      }
    }
  };

  return (
    <div id="main">
      <ImportNovel
        user={user}
        isOpen={importNovelOpen}
        setIsOpen={setImportNovelOpen}
        userCanImport={!isTrialImportLimitReached}
        folderParentId={folder?.id}
        hasAccessToAgenticNovelImport={subscription?.hasAccessToAgenticNovelImport}
      />

      <Container>
        <div id="menu">
          <Link
            to={"/app"}
            aria-label="Sudowrite home"
            className="logo-link"
          >
            <div className="inner">
              <h1>
                <StickyNoteAnchor anchorId={AnchorIds.LOGO} />
                <SudowriteLogo
                  className="logo"
                  data-app-home-logo="true"
                  size="large"
                />
              </h1>
            </div>
          </Link>

          <div className="left-container">
            {!newProjectFlow && (
              <>
                <ActionMenu
                  actions={actionsNew}
                  position="bottom-start"
                >
                  {(openMenu) => (
                    <Button
                      secondaryGrey={true}
                      className="use-component-styles"
                      onClick={openMenu}
                      aria-label="Create New"
                      disabled={deleted}
                    >
                      <StickyNoteAnchor anchorId={AnchorIds.NEW_PROJECT_BUTTON} />
                      <SVG
                        src="/assets/add.svg"
                        width={20}
                        height={20}
                        className="add"
                      />
                      <p>New</p>
                    </Button>
                  )}
                </ActionMenu>
                {showImportLimitTooltip ? (
                  <SudoTooltip
                    text={TRIAL_IMPORT_LIMIT_TOOLTIP}
                    position="bottom"
                    delay={150}
                    triggerStyle={{ display: "inline-flex" }}
                  >
                    <Button
                      secondaryGrey={true}
                      onClick={() => setImportNovelOpen(true)}
                      className="use-component-styles"
                      aria-label={TRIAL_IMPORT_LIMIT_TOOLTIP}
                      title={TRIAL_IMPORT_LIMIT_TOOLTIP}
                      disabled={importButtonDisabled}
                    >
                      <StickyNoteAnchor anchorId={AnchorIds.IMPORT_NOVEL_BUTTON} />
                      <SVG
                        src="/assets/import.svg"
                        height={20}
                        className="import"
                      />
                      <p>Import Novel</p>
                    </Button>
                  </SudoTooltip>
                ) : (
                  <Button
                    secondaryGrey={true}
                    onClick={() => setImportNovelOpen(true)}
                    className="use-component-styles"
                    aria-label="Import Your Story"
                    disabled={importButtonDisabled}
                  >
                    <StickyNoteAnchor anchorId={AnchorIds.IMPORT_NOVEL_BUTTON} />
                    <SVG
                      src="/assets/import.svg"
                      height={20}
                      className="import"
                    />
                    <p>Import Novel</p>
                  </Button>
                )}
              </>
            )}
          </div>

          <div className="right-container">
            {isTourActive && activeTourId !== TourIds.FIND_TOUR_AGAIN && activeTourId !== TourIds.CHAT_ONBOARDING && activeTourId !== TourIds.SERIES_FOLDER_INTRO && !(activeTourId === TourIds.STORY_BIBLE && currentStepIndex === 0) && <ExitTourButton />}
            {(!isTourActive || (activeTourId !== TourIds.WRITE_REWRITE && activeTourId !== TourIds.PLUGINS_QUICKSTART && activeTourId !== TourIds.FEEDBACK)) && (
              <>
                {!showSearch && <FeaturebaseChangelog />}

                <AnimatePresence>
                  {showSearch && (
                    <motion.input
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.9, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      layout
                      className="search-input"
                      placeholder="Search"
                      type="text"
                      autoFocus
                      onBlur={() => {
                        if (!searchQuery) {
                          setHideSearch(true);
                        }
                      }}
                      value={searchQuery}
                      onFocus={() => setHideSearch(false)}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                      }}
                    />
                  )}
                </AnimatePresence>
                <AnimatePresence>
                  {!showSearch && (
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.9, opacity: 0, transition: { duration: 0.0 } }}
                      transition={{ duration: 0.15 }}
                    >
                      <Button
                        secondaryGrey={true}
                        onClick={() => setShowSearch(true)}
                        iconSize={24}
                        className="use-component-styles"
                        aria-label="Search"
                      >
                        <SVG
                          src="/assets/search-v2.svg"
                          className="search"
                          width={24}
                          height={24}
                        />
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>

                {helpMenu}
                {settingsPanel}
              </>
            )}
          </div>
        </div>
        <div className="inner">
          <DndContext
            onDragEnd={handleDragEnd}
            collisionDetection={pointerWithin}
            sensors={sensors}
          >
            {(folder || searchQuery) && (
              <div className={classNames("header", { hide: isHomePage && !searchQuery && !deleted })}>
                {!searchQuery && (
                  <div className="breadcrumbs">
                    {deleted && !isHomePage && (
                      <>
                        <BreadcrumbItemTrash onClick={() => setFolderId(null)} />
                        <SVG
                          className="separator"
                          alt="Sudowrite Logo"
                          src="/assets/down-chevron.svg"
                        />
                      </>
                    )}
                    {parents.map((parent) => (
                      <React.Fragment key={parent.id}>
                        <BreadcrumbItem
                          folder={parent}
                          onClick={() => setFolderId(parent.id)}
                        />
                        <SVG
                          className="separator"
                          alt="Sudowrite Logo"
                          src="/assets/down-chevron.svg"
                        />
                      </React.Fragment>
                    ))}
                  </div>
                )}

                <div className="title-container">
                  {editingFolderId ? (
                    <textarea
                      value={folderTitle}
                      onChange={(e) => setFolderTitle(e.target.value)}
                      onBlur={() => {
                        renameFolder(editingFolderId, folderTitle);
                        setEditingFolderId(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.target.blur();
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      rows={folderTitle.length > 20 ? 2 : 1}
                      spellCheck={false}
                    />
                  ) : (
                    <div
                      className="title"
                      title={searchQuery || folder?.title}
                      onClick={() => {
                        if (!searchQuery && !isHomePage) {
                          setEditingFolderId(folder?.id);
                          setFolderTitle(folder?.title);
                        }
                      }}
                    >
                      {searchQuery ? `Search ${deleted ? "deleted" : ""}: "${searchQuery}"` : isHomePage && deleted ? "Deleted Projects" : folder?.title}
                    </div>
                  )}
                  <div className="buttons-container">
                    {!searchQuery && !deleted && (
                      <DotsActionMenu
                        actions={actionsHeader}
                        iconSize={16}
                        position="bottom-start"
                        ariaLabel={`More options for ${folder?.title || "folder"}`}
                      />
                    )}
                    <IconButton
                      className="close"
                      onClick={(event) => {
                        if (searchQuery) {
                          setSearchQuery("");
                          setShowSearch(false);
                        } else if (deleted && isHomePage) {
                          history.push("/app");
                        } else {
                          setFolderId(parents.length > 0 ? parents[parents.length - 1].id : null);
                        }
                        event.stopPropagation();
                      }}
                      src="/assets/close.svg"
                      smartFill={false}
                    />
                  </div>
                </div>

                {folder && (
                  <div className="subtitle-container">
                    <div className="subtitle">{compact([formatCount(projectCount, "project"), formatCount(folderCount, "folder")]).join(", ")}</div>
                    {!searchQuery && <div className="subtitle">Last edited {moment(folder.updated_at).fromNow()}</div>}
                  </div>
                )}
              </div>
            )}

            {folder && folder.series_id && (
              <SeriesContinuity
                folder={folder}
                projects={folderProjects}
              />
            )}

            <FoldersContainer>
              {folder?.series_id && projectCount === 0 && (
                <SeriesFolderProjectsAnchor aria-hidden="true">
                  <StickyNoteAnchor anchorId={AnchorIds.SERIES_FOLDER_PROJECTS_AREA} />
                </SeriesFolderProjectsAnchor>
              )}
              {getExtendedEntries().map((entry) => {
                if (entry.type === "new-project") {
                  return (
                    <NewProjectCard
                      key="new-project"
                      importDisabled={isTrialImportLimitReached}
                      importDisabledReason={TRIAL_IMPORT_LIMIT_TOOLTIP}
                      isInsideSeries={Boolean(folder.series_id)}
                      onCreateBlank={() => {
                        AppEventPublisher.projects.createProject({ uiSource: "+ New Card" });
                        createBlankProject();
                      }}
                      onGenerate={async () => {
                        setToast(<span role="status" aria-label="Setting up your project...">Setting up your project...</span>, { durationMs: Infinity });
                        AppEventPublisher.projects.generateProject({ uiSource: "+ New Card" });
                        await createGeneratedProject({ create, dismissToast, setToast });
                      }}
                      onImport={() => {
                        AppEventPublisher.projects.importProject({ uiSource: "+ New Card" });
                        setImportNovelOpen(true);
                      }}
                      onCreateFolder={async () => {
                        AppEventPublisher.projects.createFolder({ uiSource: "+ New Card" });
                        const createdFolder = await createFolder(folder?.id);
                        if (createdFolder) {
                          openCreatedFolder(createdFolder, false);
                        }
                      }}
                      onCreateSeries={async () => {
                        AppEventPublisher.projects.createSeries({ uiSource: "+ New Card" });
                        const showSeriesFolderIntro = !hasExistingSeries(foldersById);
                        const seriesFolder = await createFolder(folder?.id, true);
                        if (seriesFolder) {
                          openCreatedFolder(seriesFolder, true, { showSeriesFolderIntro });
                        }
                      }}
                    />
                  );
                } else if (entry.type === "project") {
                  return entry.deleted ? null : (
                    <ProjectCard
                      attachTourAnchor={emptySignupProjectId === entry.project.firebase_id}
                      key={entry.project.id}
                      project={entry.project}
                      foldersById={foldersById}
                      foldersByParentId={foldersByParentId}
                      onMoveToFolder={(folderId) => {
                        if (folder && folder.series_id) {
                          // if we are in a series, optimistic update of series contents in continuity
                          const updatedContents = contents.filter((item) => item.projectId !== entry.project.firebase_id);
                          setContents(updatedContents);
                        }
                        moveProject(entry.project.id, folderId);
                      }}
                      disableWelcomeCard={disableWelcomeCard}
                      onRestore={(project) => {
                        restoreProject(project);
                      }}
                      onArchiveDelete={(project) => {
                        archiveDeleteProject(project);
                      }}
                      onDelete={() => {
                        const restore = async () => {
                          restoreProject(entry.project);

                          setToast(
                            <>
                              Project "{entry.project.title}" restored. <Link to={`/app/project/${entry.project.firebase_id}`}>Open it</Link>
                            </>,
                          );
                        };

                        if (folder && folder.series_id) {
                          // if we are in a series, optimistic update of series contents in continuity
                          const updatedContents = contents.filter((item) => item.projectId !== entry.project.firebase_id);
                          setContents(updatedContents);
                        }

                        deleteProject(entry.project);
                        setToast(
                          <>
                            Project "{entry.project.title}" deleted. <a onClick={restore}>Undo</a>
                          </>,
                        );
                      }}
                      onDuplicate={() => {
                        duplicateProject(entry.project)
                          .then(() => {
                            setToast(`Project "${entry.project.title}" duplicated`);
                          })
                          .catch(() => {
                            setToast(`Failed to duplicate project "${entry.project.title}". Please try again.`);
                          });
                      }}
                      onCancelImport={(preserveProject) => {
                        cancelImport(entry.project, preserveProject).then(() => {
                          setToast(`Import of project "${entry.project.title}" cancelled`);
                        });
                      }}
                      disabled={
                        entry.project.is_importing &&
                        !isImportSpeedPerceptionActive(importSpeedPerceptionExperiment, entry.project.id)
                      }
                      onSetCover={setProjectCover}
                      onRemoveCover={removeProjectCover}
                      onRename={renameProject}
                      mutate={mutate}
                    />
                  );
                } else if (entry.type === "folder") {
                  const folderEntries = getEntries(foldersById, projectsById, foldersByParentId, projectsByFolderId, entry.folder, deleted);
                  const projectCount = getNestedProjects(foldersByParentId, projectsByFolderId, entry.folder, deleted).length;

                  return (
                    <FolderCard
                      key={entry.folder.id}
                      folder={entry.folder}
                      entries={folderEntries}
                      projectCount={projectCount}
                      foldersById={foldersById}
                      foldersByParentId={foldersByParentId}
                      onOpen={async () => {
                        setFolderId(entry.folder.id);
                        setSearchQuery("");
                        setShowSearch(false);
                      }}
                      onDelete={() => {
                        const restore = async () => {
                          restoreFolder(entry.folder.id);

                          setToast(
                            <>
                              Folder "{entry.folder.title}" restored. <Link onClick={() => setFolderId(entry.folder.id)}>Open it</Link>
                            </>,
                          );
                        };

                        deleteFolder(entry.folder.id);

                        setToast(
                          <>
                            Folder "{entry.folder.title}" deleted. <a onClick={restore}>Undo</a>
                          </>,
                        );
                      }}
                      onArchiveDelete={() => {
                        archiveDeleteFolder(entry.folder.id);
                      }}
                      onRestore={() => {
                        restoreFolder(entry.folder.id);
                      }}
                      onMoveToFolder={(folderId) => {
                        moveFolder(entry.folder.id, folderId);
                      }}
                      onRename={(title) => {
                        renameFolder(entry.folder.id, title);
                      }}
                    />
                  );
                } else if (entry.type === "stack") {
                  return (
                    <CardStack
                      key="stack"
                      cards={enabledPromoCards}
                      animateEntrance={isPromoStackRestored}
                      renderCard={(promoCard, close) => (
                        <PromoCard
                          promoCard={promoCard}
                          isAnyImporting={isAnyImporting}
                          onClose={close}
                        />
                      )}
                      onDismiss={hidePromoCard}
                    />
                  );
                }
                return null;
              })}
            </FoldersContainer>
          </DndContext>
        </div>
      </Container>
    </div>
  );
}
